/**
 * Build-clock indirection for render-time "now" reads.
 *
 * Statically prerendered pages bake clock-derived content (default buffer
 * series month, market open/closed, outcome-period math) from the clock at
 * BUILD time. The browser then hydrates at VISIT time — so any component that
 * read the wall clock directly during render would produce different markup
 * than the HTML it is hydrating, a guaranteed mismatch.
 *
 * The contract:
 *   - The prerenderer stamps ONE build-instant ISO string per rendered page:
 *     set on `globalThis.__SSG_NOW__` for its own render pass, and embedded in
 *     the page next to `__RQ_STATE__`/`__SSG_PATH__` so the hydration render
 *     reads the SAME instant. Head-only shells (/_shell, /404.html) carry none.
 *   - Every render-path "now" read goes through {@link renderNow}: the build
 *     instant when present, the real clock otherwise (plain SPA loads, client
 *     navigations, tests — all unchanged).
 *   - `main.tsx` calls {@link releaseBuildClock} immediately after hydration,
 *     after which everything reads real visit time; components whose baked
 *     value must self-correct re-resolve in a post-mount effect with an
 *     explicit `new Date()` (order-independent of the release).
 */

export function renderNow(): Date {
  const raw = (globalThis as { __SSG_NOW__?: unknown }).__SSG_NOW__;
  if (typeof raw === "string" && raw.length > 0) {
    const pinned = new Date(raw);
    if (!Number.isNaN(pinned.getTime())) return pinned;
  }
  return new Date();
}

/** Drop the embedded build instant — called once, right after hydration. */
export function releaseBuildClock(): void {
  (globalThis as { __SSG_NOW__?: unknown }).__SSG_NOW__ = undefined;
}
